// src/components/FSETracking/SessionMap.js
// Draws an FSE's tracked route (start marker, live/last marker, polyline)
// using Leaflet + OpenStreetMap tiles. Leaflet is loaded lazily from a CDN
// the first time a map is needed, so the project doesn't need a new npm
// dependency (`leaflet`/`react-leaflet`) just for this one screen.
import React, { useEffect, useRef, useState } from 'react';

const LEAFLET_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
const LEAFLET_JS = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';

let leafletLoadPromise = null;
function loadLeaflet() {
  if (window.L) return Promise.resolve(window.L);
  if (leafletLoadPromise) return leafletLoadPromise;

  leafletLoadPromise = new Promise((resolve, reject) => {
    if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = LEAFLET_CSS;
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = LEAFLET_JS;
    script.async = true;
    script.onload = () => resolve(window.L);
    script.onerror = () => reject(new Error('Failed to load map library'));
    document.body.appendChild(script);
  });
  return leafletLoadPromise;
}

const ACCENT = '#dc2626';

/**
 * props:
 *  - route: [{ latitude, longitude }]
 *  - startLocation: { latitude, longitude } | null
 *  - height: number (px)
 */
const SessionMap = ({ route = [], startLocation, height = 360 }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const layersRef = useRef({ line: null, start: null, current: null });
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState(false);

  // Init map once
  useEffect(() => {
    let cancelled = false;
    loadLeaflet()
      .then((L) => {
        if (cancelled || !containerRef.current || mapRef.current) return;
        const map = L.map(containerRef.current, {
          zoomControl: true,
          attributionControl: true,
        }).setView([20.5937, 78.9629], 5); // India default view

        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(map);

        mapRef.current = map;
        setReady(true);
      })
      .catch(() => setLoadError(true));

    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  // Redraw route whenever data changes
  useEffect(() => {
    if (!ready || !window.L || !mapRef.current) return;
    const L = window.L;
    const map = mapRef.current;

    // Clear previous layers
    Object.values(layersRef.current).forEach((layer) => layer && map.removeLayer(layer));
    layersRef.current = { line: null, start: null, current: null };

    const points = (route || [])
      .filter((p) => typeof p.latitude === 'number' && typeof p.longitude === 'number')
      .map((p) => [p.latitude, p.longitude]);

    const start = startLocation?.latitude != null
      ? [startLocation.latitude, startLocation.longitude]
      : points[0];

    if (start) {
      layersRef.current.start = L.circleMarker(start, {
        radius: 7,
        color: '#16a34a',
        fillColor: '#16a34a',
        fillOpacity: 1,
        weight: 2,
      }).bindTooltip('Start').addTo(map);
    }

    if (points.length >= 2) {
      layersRef.current.line = L.polyline(points, {
        color: ACCENT,
        weight: 4,
        opacity: 0.85,
        lineJoin: 'round',
      }).addTo(map);
      map.fitBounds(layersRef.current.line.getBounds(), { padding: [40, 40] });
    } else if (start) {
      map.setView(start, 15);
    }

    const last = points[points.length - 1];
    if (last) {
      layersRef.current.current = L.circleMarker(last, {
        radius: 8,
        color: ACCENT,
        fillColor: '#fff',
        fillOpacity: 1,
        weight: 3,
      }).bindTooltip('Current / Last point').addTo(map);
    }
  }, [ready, route, startLocation]);

  if (loadError) {
    return (
      <div style={{
        height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-muted)', fontSize: 13, background: 'var(--bg-input)',
        borderRadius: 'var(--radius)',
      }}>
        Couldn't load the map. Check your connection and try again.
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      style={{
        height,
        width: '100%',
        borderRadius: 'var(--radius)',
        overflow: 'hidden',
        border: '1px solid var(--border)',
        background: 'var(--bg-input)',
      }}
    />
  );
};

export default SessionMap;
